import React, { useEffect, useState } from 'react';
import { useScrollPosition } from './useScrollPosition';
import './ContentMenu.styles.scss';

interface MenuItem {
  id: string;
  text: string;
  level: number;
  offset: number;
}

export interface ContentMenuProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The application's current pathname, supplied via router hook or parent component.
   * This value ensures the DOM scanning effect reruns on each page load.
   */
  pathname: string;
  /** A string title or custom React Element that sits above the ToC links
   * @default "Content"
   */
  title?: string | React.ReactElement;
  /** An array of all heading tags that the component should include as navigation links
   * @default ["h2", "h3", "h4"]
   */
  headings?: ('h2' | 'h3' | 'h4' | 'h5' | 'h6')[] | 'all';
  /** If true, links will be indented according to the heading tag
   * @default true
   */
  indent?: boolean;
  /** A number (in pixels) that sets the indent padding for each nested heading.
   * @default 10
   */
  indentSize?: number;
  /** Callback function that tells the parent how many valid headings are on the page */
  headingCount: (count: number) => void;
}

/**
 * Utility component to remove any leading or trailing HTML tags
 * @param text a string that may include HTML tags
 * @return a sanitized version of the string
 */
function sanitize(text: string) {
  return text.split('<').find((i) => !i.startsWith('<'));
}

/**
 * The `TableofContents` component queries the DOM for all heading tags that
 * have an ID. It then creates an indented / scroll activated list of heading links.
 */
export const ContentMenu = ({
  headings = ['h2', 'h3', 'h4'],
  indent = true,
  indentSize = 10,
  className,
  pathname,
  headingCount,
}: ContentMenuProps) => {
  const [menuItems, setMenu] = useState<MenuItem[]>([]);
  const [activeNode, setActiveNode] = useState<number | null>(null);

  /**
   * Query DOM for applicable heading elements
   */
  useEffect(() => {
    const activeHeadings = headings !== 'all' ? headings : allHeadings;
    const selectors = activeHeadings.map((h) => `main ${h}`).join(', ');
    const nodes: HTMLElement[] = Array.from(
      document.querySelectorAll(selectors)
    );

    if (nodes.length) {
      const menu = nodes.reduce<any>(
        (filtered, { id, innerHTML, innerText, offsetTop, tagName }) => {
          if (id) {
            filtered.push({
              text: sanitize(
                !innerHTML.includes('<!--') ? innerHTML : innerText
              ),
              id,
              level: getLevel(tagName),
              offset: offsetTop,
            });
          }
          return filtered;
        },
        []
      );
      setMenu(menu);
    } else {
      setMenu([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Handle Scroll position
  useScrollPosition(
    ({ currPos, prevPos }) => {
      const isDownScroll = currPos > prevPos;

      /**
       * Reset activeNode if scroll position is above first selector
       */
      if (activeNode !== undefined && currPos < menuItems[0]?.offset) {
        return setActiveNode(null);
      }

      if (currPos >= menuItems[0].offset) {
        if (activeNode === null) {
          /**
           * Check if scroll is between first 2 heading nodes
           */
          if (menuItems.length > 1 && currPos <= menuItems[1]?.offset) {
            return setActiveNode(0);
          } else {
            /**
             * Else find the nearest offset
             * Used for fresh page loads if scroll is not at top of document
             */
            const offsets = menuItems.map((i) => i.offset);
            const closest = offsets.reduce((a, b) => {
              return Math.abs(b - currPos) < Math.abs(a - currPos) ? b : a;
            });
            return setActiveNode(
              menuItems.findIndex((i) => i.offset === closest)
            );
          }
        }

        if (isDownScroll) {
          /**
           * If scrolling down, compare current node offset with the next offset
           */
          if (
            activeNode !== menuItems.length - 1 &&
            currPos >= menuItems[activeNode + 1].offset
          ) {
            return setActiveNode(activeNode + 1);
          }
        } else {
          /**
           * If scrolling up, compare current node offset with previous offset
           */
          if (currPos <= menuItems[activeNode]?.offset) {
            return setActiveNode(activeNode - 1);
          }
        }
      }
    },
    200,
    menuItems.length ? true : false
  );

  useEffect(() => {
    if (!menuItems) return;
    headingCount(menuItems.length);
  }, [menuItems, headingCount]);

  return (
    <div className={`toc ${className}`}>
      <div className="flex toc-title">Page Content</div>
      <ul className="toc-headings">
        {menuItems.length
          ? menuItems.map(({ id, text, level }: MenuItem, index) => (
              <li
                key={index}
                className={`level-${level}`}
                style={{
                  paddingLeft: indent ? indentSize * level : undefined,
                }}>
                <a
                  className={activeNode === index ? 'active' : undefined}
                  onClick={() => setActiveNode(index)}
                  href={`#${id}`}>
                  {text}
                </a>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
};

ContentMenu.displayName = 'ContentMenu';

const allHeadings = ['h2', 'h3', 'h4', 'h5', 'h6'];

function getLevel(lvl: string) {
  switch (lvl) {
    case 'H3':
      return 1;
    case 'H4':
      return 2;
    case 'H5':
      return 3;
    case 'H6':
      return 4;
    case 'H2':
    default:
      return 0;
  }
}
